/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import isNil from 'lodash/isNil';
import get from 'lodash/get';
import find from 'lodash/find';
import includes from 'lodash/includes';
import { TYPE_OF_USE_DAY, TYPE_OF_USE_OVERNIGHT } from '../constants';

/**
 * Finds and returns the value of an attribute with `attrName` in the `campsite` details data
 *
 * 1) look for the attribute first with the `path` provided (this usually points to the
 * 'equipment_details_map', 'site_details_map', and 'permitted_equipment'. that are
 * configurable from the HUB)
 *
 * 2) if the `path` provided doesn't find the attribute, fall back to find the attribute
 * in the 'attributes' mapping the the `campsite` details object. The values in there are
 * imported from the old system and are not configurable, hence it's the backup option
 *
 * 3) if attribute still can't be accessed, return the `defaultValue`
 *
 * @param {object} campsite - campsite details object
 * @param {string} path - path to access the target attribute in the `campsite` details data
 * @param {string} attrName - name of the attribute
 * @param {string} defaultValue - default value if no result is found
 */
export function findCampsiteAttributeValue(campsite, path, attrName, defaultValue) {
    let value;

    // (1) Prefered map path access
    let settingValue = get(campsite, `${path}.attribute_value`, null);

    // (2) Backup path access to non-configurable 'attributes' map
    if (isNil(settingValue)) {
        const attrObj = find(get(campsite, 'attributes', []), {
            attribute_name: attrName,
        });
        settingValue = isNil(attrObj) ? null : attrObj.attribute_value;
    }

    if (!isNil(settingValue)) {
        value = settingValue;
    }
    // (3) return default value if attribute still not found
    else {
        value = defaultValue;
    }

    return value;
}

export const getCampsiteProperties = (campsites = []) => {
    let hasLTSSites = false;
    let hasDayUseSites = false;
    let hasGroupSites = false;
    let hasStandardSites = false;
    let hasOvernightCampsites = false;
    let campsiteTypeSelected = null;

    campsites.forEach((campsite) => {
        if (includes(campsite.type, 'GROUP')) {
            hasGroupSites = true;
        } else {
            hasStandardSites = true;
        }

        if (campsite.type_of_use === TYPE_OF_USE_OVERNIGHT) {
            hasOvernightCampsites = true;
        }
        if (campsite.type_of_use === TYPE_OF_USE_DAY) {
            hasDayUseSites = true;
        }
        // if campsite is LTS
        if (get(campsite, 'facilityRules.allowRecurringPayment.value', 0) === 1) {
            hasLTSSites = true;
        }
    });

    // we perform this check to know which booking window to use in the calendar
    if (hasGroupSites && !hasStandardSites) {
        campsiteTypeSelected = 'GROUP';
    } else if (!hasGroupSites && hasStandardSites) {
        campsiteTypeSelected = 'STANDARD';
    }

    return {
        hasLTSSites,
        hasDayUseSites,
        hasGroupSites,
        hasStandardSites,
        hasOvernightCampsites,
        campsiteTypeSelected,
    };
};

export const getCampsitesWithLocation = (campsites = []) => {
    return campsites.filter((campsite) => campsite.latitude && campsite.longitude);
};
